import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowRightOutlined,
  PlusOutlined,
  Dropdown,
} from "@/config/antd";
import {
  updateStep,
  setCurrentStep,
  selectSubEventStep2,
} from "@/store/redux/slices/event/sub_event_slice";
import Back from "@/components/specific/Back";
import Button from "@/components/common/Button";
import Input from "@/components/common/inputs/Input";
import TimeRangeInput from "@/components/specific/Time_Range_Input";
import Success from "@/components/common/alerts/Success";
import { useLoader } from "@/hooks/LoaderProvider";
import MemberPicker from "@/components/specific/Member_Picker";
import InviteMembersDialog from "@/ui/Invite_Members";
import Delete from "@/components/common/alerts/Delete";
import CustomMoreOptions from "@/components/specific/Custom_More_Options";

const Step2AddSubEvent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { eventId } = useParams();
  const { showLoader, hideLoader } = useLoader();

  // Get initial state from Redux
  const step2Data = useSelector(selectSubEventStep2);

  // Form state initialized from Redux store
  const [schedules, setSchedules] = useState(step2Data.schedules || []);
  const [images] = useState(step2Data.images || []);

  // Add a state to track if we're editing a schedule
  const [editingScheduleId, setEditingScheduleId] = useState(null);

  const [formData, setFormData] = useState({
    schedule: {
      name: step2Data.schedule?.name || "",
      speakers: step2Data.schedule?.speakers || [],
      duration: {
        startTime: step2Data.schedule?.duration?.startTime || "",
        endTime: step2Data.schedule?.duration?.endTime || "",
      },
    },
    isInviteMembersModalVisible: false,
    isDeleteModalVisible: false,
    isSuccessModalVisible: false,
    memberToDelete: null,
  });

  // Roles for speakers
  const roleOptions = [
    { value: "speaker", label: "Speaker" },
    { value: "moderator", label: "Moderator" },
    { value: "panelist", label: "Panelist" },
  ];

  const handleTimeFromChange = (time) => {
    const date = new Date(`2000-01-01T${time}`);
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    setFormData((prev) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        duration: {
          ...prev.schedule.duration,
          startTime: formattedTime,
        },
      },
    }));
  };

  const handleTimeToChange = (time) => {
    const date = new Date(`2000-01-01T${time}`);
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    setFormData((prev) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        duration: {
          ...prev.schedule.duration,
          endTime: formattedTime,
        },
      },
    }));
  };

  const handleMembersSelected = (newMembers) => {
    setFormData((prev) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        speakers: [
          ...prev.schedule.speakers,
          ...newMembers.filter(
            (member) =>
              !prev.schedule.speakers.some(
                (existing) => existing.id === member.id
              )
          ),
        ],
      },
      isInviteMembersModalVisible: false,
    }));
  };

  const handleDeleteMember = (member) => {
    setFormData((prev) => ({
      ...prev,
      memberToDelete: member.id,
      isDeleteModalVisible: true,
    }));
  };

  const handleDeleteConfirm = () => {
    showLoader();

    setTimeout(() => {
      setFormData((prev) => ({
        ...prev,
        schedule: {
          ...prev.schedule,
          speakers: prev.schedule.speakers.filter(
            (speaker) => speaker.id !== prev.memberToDelete
          ),
        },
        isDeleteModalVisible: false,
        isSuccessModalVisible: true,
        memberToDelete: null,
      }));
      hideLoader();
    }, 2000);
  };

  const handleUpdateMember = (memberId, newRole) => {
    setFormData((prev) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        speakers: prev.schedule.speakers.map((speaker) =>
          speaker.id === memberId ? { ...speaker, role: newRole } : speaker
        ),
      },
    }));
  };

  const handleBack = () => {
    navigate(`/dashboard/events/${eventId}/add-sub-event/step-1`);
  };

  const handleModifySchedule = (schedule) => {
    setFormData((prev) => ({
      ...prev,
      schedule: {
        name: schedule.title,
        speakers: schedule.speakers,
        duration: {
          startTime: schedule.duration.startTime,
          endTime: schedule.duration.endTime,
        },
      },
    }));

    setEditingScheduleId(schedule.id);
  };

  const handleSaveSchedule = () => {
    if (!isFormValid) return;

    showLoader();

    if (editingScheduleId) {
      // We're editing an existing schedule
      const updatedSchedules = schedules.map((schedule) =>
        schedule.id === editingScheduleId
          ? {
              ...schedule,
              title: formData.schedule.name,
              speakers: formData.schedule.speakers,
              duration: {
                startTime: formData.schedule.duration.startTime,
                endTime: formData.schedule.duration.endTime,
              },
            }
          : schedule
      );

      setTimeout(() => {
        setSchedules(updatedSchedules);

        dispatch(
          updateStep({
            step: "step2",
            data: {
              schedule: formData.schedule,
              schedules: updatedSchedules,
              images,
            },
          })
        );

        setFormData((prev) => ({
          ...prev,
          schedule: {
            name: "",
            speakers: [],
            duration: {
              startTime: "",
              endTime: "",
            },
          },
          isSuccessModalVisible: true,
        }));
        setEditingScheduleId(null);

        hideLoader();
      }, 1000);
    } else {
      // Adding a new schedule
      const newSchedule = {
        id: Date.now(),
        title: formData.schedule.name,
        speakers: formData.schedule.speakers,
        duration: {
          startTime: formData.schedule.duration.startTime,
          endTime: formData.schedule.duration.endTime,
        },
      };

      setTimeout(() => {
        setSchedules((prev) => [...prev, newSchedule]);

        dispatch(
          updateStep({
            step: "step2",
            data: {
              schedule: formData.schedule,
              schedules: [...schedules, newSchedule],
              images,
            },
          })
        );

        setFormData((prev) => ({
          ...prev,
          schedule: {
            name: "",
            speakers: [],
            duration: {
              startTime: "",
              endTime: "",
            },
          },
          isSuccessModalVisible: true,
        }));

        hideLoader();
      }, 1000);
    }
  };

  const handleProceed = () => {
    showLoader();
    try {
      // Include all schedules in the data passed to step 3
      dispatch(
        updateStep({
          step: "step2",
          data: {
            schedule: formData.schedule,
            schedules: schedules,
            images,
          },
        })
      );

      dispatch(setCurrentStep(3));

      setTimeout(() => {
        hideLoader();
        navigate(`/dashboard/events/${eventId}/add-sub-event/step-3`);
      }, 2000);
    } catch (error) {
      console.error("Failed to process step 2:", error);
      hideLoader();
    }
  };

  const isFormValid =
    formData.schedule.name.trim() !== "" &&
    formData.schedule.speakers.length > 0 &&
    formData.schedule.duration.startTime !== "" &&
    formData.schedule.duration.endTime !== "";

  const handleRemoveSchedule = (scheduleId) => {
    showLoader();

    setTimeout(() => {
      const updatedSchedules = schedules.filter(
        (schedule) => schedule.id !== scheduleId
      );
      setSchedules(updatedSchedules);

      dispatch(
        updateStep({
          step: "step2",
          data: {
            schedule: formData.schedule,
            schedules: updatedSchedules,
            images,
          },
        })
      );

      hideLoader();
    }, 500);
  };

  // Customize the button text based on whether we're editing or adding
  const saveButtonText = editingScheduleId
    ? "Update schedule"
    : "Save and add new schedule";

  return (
    <div className="w-full my-5 text-left">
      <Back text="Back" onClick={handleBack} />

      <div className="lg:p-5 my-5 rounded-lg lg:border">
        <h2 className="mb-2 font-semibold text-bold text-darkGrey">
          Set up the schedules within this sub-event
        </h2>
        <p className="mb-2 text-small text-darkGrey">
          Schedules are timed tasks within each sub-events
        </p>
        <p className="mb-4 text-small text-darkGrey italic font-bold">
          NOTE: You can add as many schedules as possible by clicking on the
          &quot;Add More&quot; button
        </p>

        {/* Display existing schedules */}
        {schedules.length > 0 && (
          <div className="mb-8">
            {schedules.map((schedule) => (
              <div
                key={schedule.id}
                className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center gap-4">
                  <div className="flex flex-col">
                    <span className="font-none text-primary text-small-xs">
                      TIMELINE
                    </span>
                    <span className="font-bold text-bold">
                      {schedule.duration.startTime}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="font-bold text-regular">
                      {schedule.title}
                    </span>
                    <span className="text-lighterGrey text-small-xs font-bold">
                      {schedule.speakers.length > 0
                        ? schedule.speakers[0].name
                        : "No speaker assigned"}
                    </span>
                  </div>
                </div>
                <div className="flex items-center">
                  <Button
                    text="REMOVE"
                    className="mr-2 border border-primary text-small w-[85px] h-[34px]"
                    customBgColor="white"
                    customTextColor="primary"
                    onClick={() => handleRemoveSchedule(schedule.id)}
                    fontWeight="none"
                  />
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: "1",
                          label: "Modify schedule",
                          onClick: () => handleModifySchedule(schedule),
                        },
                      ],
                    }}
                    trigger={["click"]}>
                    <div onClick={(e) => e.preventDefault()}>
                      <CustomMoreOptions
                        variant="filled"
                        orientation="vertical"
                        color="black"
                      />
                    </div>
                  </Dropdown>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Schedule Form */}
        <div className="space-y-6">
          <div>
            <h3 className="mb-2 font-semibold text-bold text-darkGrey">
              Schedule Information
            </h3>
            <Input
              label="TITLE"
              placeholder="Enter schedule title"
              value={formData.schedule.name}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  schedule: {
                    ...prev.schedule,
                    name: e.target.value,
                  },
                }))
              }
            />
          </div>

          <div>
            <h3 className="mb-2 font-semibold text-bold text-darkGrey">
              Schedule Speakers
            </h3>
            <p className="mb-4 text-small text-darkGrey">
              Select speakers for this schedule
            </p>
            <MemberPicker
              members={formData.schedule.speakers}
              roles={roleOptions}
              onOpenModal={() =>
                setFormData((prev) => ({
                  ...prev,
                  isInviteMembersModalVisible: true,
                }))
              }
              onDeleteMember={handleDeleteMember}
              onUpdateMember={handleUpdateMember}
            />
          </div>

          <div>
            <h3 className="mb-2 font-semibold text-bold text-darkGrey">
              Duration Slot
            </h3>
            <p className="mb-4 text-small text-darkGrey">
              Set the time duration for this schedule
            </p>
            <TimeRangeInput
              fromTime={formData.schedule.duration.startTime}
              toTime={formData.schedule.duration.endTime}
              onFromChange={handleTimeFromChange}
              onToChange={handleTimeToChange}
            />
          </div>

          {/* Update the button text based on edit state */}
          <Button
            text={saveButtonText}
            icon={<PlusOutlined />}
            onClick={handleSaveSchedule}
            disabled={!isFormValid}
            className="w-full border border-primary cursor-pointer text-bold h-[36px]"
            customBgColor="white"
            customTextColor="primary"
            iconPosition="left"
            fontWeight="none"
          />

          <Button
            text="Proceed"
            icon={<ArrowRightOutlined />}
            onClick={handleProceed}
            disabled={!isFormValid && schedules.length === 0}
            color="primary"
            className={`
              w-full mt-8
              ${
                !isFormValid &&
                schedules.length === 0 &&
                "bg-darkBlue text-primary cursor-not-allowed"
              }
            `}
            customBgColor={
              isFormValid || schedules.length > 0 ? "primary" : "darkBlue"
            }
            customTextColor={
              isFormValid || schedules.length > 0 ? "white" : "primary"
            }
          />
        </div>
      </div>

      <InviteMembersDialog
        isVisible={formData.isInviteMembersModalVisible}
        onClose={() =>
          setFormData((prev) => ({
            ...prev,
            isInviteMembersModalVisible: false,
          }))
        }
        onMembersSelected={handleMembersSelected}
        modalTitle="Add New Speaker"
        primaryHeader="CORPORATE MEMBERS"
        showSecondaryHeader={false}
        firstSectionContent="all"
      />

      <Delete
        isVisible={formData.isDeleteModalVisible}
        onClose={() =>
          setFormData((prev) => ({ ...prev, isDeleteModalVisible: false }))
        }
        onProceed={handleDeleteConfirm}
        header={`Remove ${
          formData.schedule.speakers.find(
            (s) => s.id === formData.memberToDelete
          )?.name
        }`}
        paragraph={`Are you sure you want to remove <span class="text-primary">${
          formData.schedule.speakers.find(
            (s) => s.id === formData.memberToDelete
          )?.name
        }</span> from this schedule?`}
        proceedButtonText="Remove"
        cancelButtonText="Cancel"
      />

      <Success
        isVisible={formData.isSuccessModalVisible}
        onClose={() =>
          setFormData((prev) => ({ ...prev, isSuccessModalVisible: false }))
        }
        header={editingScheduleId ? "Schedule Updated" : "Schedule Saved"}
        paragraph={
          editingScheduleId
            ? "Your schedule has been updated successfully."
            : "Your schedule has been saved successfully."
        }
        buttonText="Close"
        onButtonClick={() =>
          setFormData((prev) => ({ ...prev, isSuccessModalVisible: false }))
        }
      />
    </div>
  );
};

export default Step2AddSubEvent;
